<template>
  <div class="lcs" :class="field.outcome ? `lcs--${field.outcome}` : ''" dir="rtl">
    <div v-for="(step, i) in steps" :key="step.key" class="lcs-step" :title="step.title">
      <span v-if="i > 0" class="lcs-line" :class="step.lineSolid ? 'lcs-line--solid' : 'lcs-line--dotted'" />

      <span class="lcs-dot" :class="[`lcs-dot--${step.tone}`, { 'lcs-dot--current': step.current }]">
        <svg v-if="step.icon === 'check'" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <path d="M3.5 8.5l3 3 6-6.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg v-else-if="step.icon === 'clock'" viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.8" />
          <path d="M8 4.8V8l2.2 1.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg v-else-if="step.icon === 'cross'" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
        </svg>
      </span>

      <span class="lcs-title" :class="`lcs-title--${step.tone}`">{{ step.label }}</span>
      <span v-if="step.time" class="lcs-time">{{ step.time }}</span>
      <span v-if="step.date" class="lcs-date">{{ step.date }}</span>
      <span v-if="step.note" class="lcs-note" :class="`lcs-note--${step.tone}`">{{ step.note }}</span>
    </div>
  </div>
</template>

<script>
const DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const persian = value => String(value ?? '').replace(/\d/g, d => DIGITS[Number(d)])

export default {
  props: ['resourceName', 'field'],

  computed: {
    steps() {
      const meta = this.field
      const at = meta.steps || {}
      const out = []

      out.push({
        key: 'created',
        label: this.__('Step created'),
        tone: 'done',
        icon: 'check',
        time: at.created ? persian(at.created.time) : null,
        date: at.created ? persian(at.created.date) : null,
      })

      const openDone = meta.openState === 'done'
      out.push({
        key: 'opened',
        label: this.__('Step opened'),
        tone: openDone ? 'done' : meta.openState === 'waiting' ? 'waiting' : 'idle',
        icon: openDone ? 'check' : meta.openState === 'waiting' ? 'clock' : null,
        // The step the point is currently on gets the halo.
        current: openDone && !at.closed,
        time: openDone ? persian(at.opened.time) : null,
        date: openDone ? persian(at.opened.date) : null,
        note: openDone ? null : meta.openState === 'waiting' ? this.__('Awaiting entry') : this.__('Not opened'),
        lineSolid: openDone,
      })

      const closed = !!at.closed
      out.push({
        key: 'closed',
        label: this.__('Step closed'),
        tone: closed ? 'done' : 'idle',
        icon: closed ? (meta.outcome === 'cancelled' ? 'cross' : 'check') : null,
        time: closed ? persian(at.closed.time) : null,
        date: closed ? persian(at.closed.date) : null,
        note: closed ? null : this.__('Still open'),
        title: closed ? meta.statusLabel : null,
        lineSolid: closed && openDone,
      })

      return out
    },
  },
}
</script>

<style>
.lcs {
  display: flex;
  min-width: 270px;
  max-width: 330px;
}

.lcs-step {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  text-align: center;
}

.lcs-dot {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 2.5px solid #b8bccb;
  border-radius: 9999px;
  background: #fff;
  color: #fff;
}

.lcs { --lcs-accent: #2f6df6; }
.lcs--profit { --lcs-accent: #22c55e; }
.lcs--loss { --lcs-accent: #ef4444; }
.lcs--cancelled { --lcs-accent: #9aa0b4; }

.lcs-dot--done { border-color: var(--lcs-accent); background: var(--lcs-accent); color: #fff; }
.lcs-dot--waiting { border-color: #2f6df6; background: #fff; color: #2f6df6; }
.lcs-dot--idle { border-color: #b8bccb; background: #fff; }

.lcs-line {
  position: absolute;
  top: 14px;
  left: 50%;
  width: 100%;
  transform: translateY(-50%);
}
.lcs-line--solid { border-top: 2.5px solid var(--lcs-accent); }
.lcs-line--dotted { border-top: 2.5px dotted #c4c8d4; }

.lcs-title {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  color: #1f2937;
}
.lcs-title--waiting { color: #2f6df6; }
.lcs-title--idle { color: #4b5563; font-weight: 600; }

.lcs-time { font-size: 13px; font-weight: 700; line-height: 18px; color: #111827; }
.lcs-date { font-size: 11px; line-height: 16px; color: #9aa0b4; }
.lcs-note { font-size: 12px; line-height: 16px; }
.lcs-note--waiting { color: #2f6df6; }
.lcs-note--idle { color: #9aa0b4; }

/* The step the point is on right now. */
.lcs-dot--current { box-shadow: 0 0 0 6px color-mix(in srgb, var(--lcs-accent) 16%, transparent); }

html.dark .lcs-dot { background: #18181b; border-color: #52525b; }
html.dark .lcs-dot--idle { border-color: #71717a; }
html.dark .lcs-dot--waiting { border-color: #60a5fa; color: #60a5fa; }
html.dark .lcs-title--idle { color: #a1a1aa; }
html.dark .lcs-note--waiting { color: #60a5fa; }
html.dark .lcs-note--idle,
html.dark .lcs-date { color: #8b8b95; }
html.dark .lcs-dot--done { border-color: var(--lcs-accent); background: var(--lcs-accent); }
html.dark .lcs-title { color: #e4e4e7; }
html.dark .lcs-title--waiting { color: #93c5fd; }
html.dark .lcs-time { color: #f4f4f5; }
html.dark .lcs-line--dotted { border-top-color: #52525b; }
</style>
